import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button } from '@mui/material';
import type { InferType } from 'yup';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';

import { CommonError, handleError } from '@/utils/errorUtils';

import { FormikForm } from '@/components/formik/FormikForm';
import { FormikSubmit } from '@/components/formik/FormikSubmit';
import { GenericModal } from '@/components/modal/GenericModal';
import { PaymentMethod } from '@/components/paymentMethod/PaymentMethod';
import { usePaymentMethod } from '@/components/paymentMethod/usePaymentMethod';
import { SideShadow } from '@/components/sideShadow/SideShadow';
import { SubmitError } from '@/components/submitError/SubmitError';

import type { TOnSubmit } from '@/types/formTypes';
import type { TAddPaymentMethodForm, TPaymentMethodListItem } from '@/types/wallet';

import { paymentMethodSchema } from '@/validation/wallet';

import { styles } from './AddPaymentMethod.styles';

type TAddPaymentMethodData = InferType<typeof paymentMethodSchema>;

type TAddPaymentMethodProps = TModalsContext & {
  onSuccess: (method: TPaymentMethodListItem) => void;
  selected: Set<number>;
};

export const AddPaymentMethod = ({ close, onSuccess, selected }: TAddPaymentMethodProps) => {
  const { t } = useTranslation();
  const { resolve } = usePaymentMethod();
  const [error, setError] = useState<string | null>(null);

  const onSubmit: TOnSubmit<TAddPaymentMethodData> = async (values, { setSubmitting }) => {
    try {
      const method = await resolve(values as TAddPaymentMethodForm);

      close('wallet.addPaymentMethod');
      onSuccess(method);
    } catch (err) {
      setSubmitting(false);

      if (err instanceof CommonError) {
        setError(t(err.messageKey));
      } else {
        setError(`${t('errors.unknown')}\n${t('errors.tryAgain')}`);
        handleError(error, 'UNHANDLED_RESPONSE');
      }
    }
  };

  return (
    <GenericModal sx={styles.modal}>
      <FormikForm validationSchema={paymentMethodSchema} onSubmit={onSubmit} sx={styles.form}>
        <SubmitError
          error={error}
          dismiss={() => setError(null)}
          sx={[styles.container, styles.error]}
        />

        <Box sx={[styles.container, styles.fields]}>
          <PaymentMethod
            disabled={selected}
            titles={{
              main: 'stationSetup.connectBank.title',
              selectExisting: 'stationSetup.connectBank.selectExisting',
              addFirst: 'stationSetup.connectBank.choosePayment',
              addNew: 'stationSetup.connectBank.addNew',
            }}
          />
        </Box>

        <Box sx={styles.footer}>
          <SideShadow side="top" value={2} />

          <Box sx={[styles.container, styles.controls]}>
            <Button variant="outlined" onClick={() => close('wallet.addPaymentMethod')}>
              {t('common.cancel')}
            </Button>

            <FormikSubmit>{t('common.add')}</FormikSubmit>
          </Box>
        </Box>
      </FormikForm>
    </GenericModal>
  );
};
